import { supabase } from '../lib/supabase';
import type {
  UserProfile,
  CallAgentSettings,
  WhatsAppAgentSettings,
  WhatsAppTemplates,
  WhatsAppTemplateKey,
  EmailAgentSettings,
  EmailTemplates,
  EmailTemplateKey,
  LinkedInAgentSettings,
  LinkedInTemplates,
  LinkedInTemplateKey,
  CampaignStepData,
  LinkedInConnection,
  AgentScriptGenerationRequest,
  AgentScriptGenerationResponse,
  WaTemplateSubmission,
  WaTemplateApprovalStatus,
} from '../types/settings';
import { WA_TEMPLATE_DEFINITIONS, EMAIL_TEMPLATE_DEFINITIONS, LI_TEMPLATE_DEFINITIONS } from '../types/settings';
import { normalizeToneValue, getToneInstructions } from '../shared/toneInstructions';
import { getLocalUser } from '../utils/authHelpers';

async function findTeammateCompanyInfo(userEmail: string) {
  const { data: teammates } = await supabase
    .from('client_users')
    .select('client_id')
    .eq('email', userEmail)
    .limit(1)
    .maybeSingle();

  if (!teammates?.client_id) return null;

  const { data: orgMembers } = await supabase
    .from('client_users')
    .select('email')
    .eq('client_id', teammates.client_id)
    .neq('email', userEmail);

  if (!orgMembers || orgMembers.length === 0) return null;

  const emails = orgMembers.map(m => m.email);

  const { data: companyInfo } = await supabase
    .from('user_settings')
    .select('company_name, company_logo_url, company_website, country, company_industry, icp_config, daily_call_limit, daily_lead_generation_limit, company_profile_url, company_profile_material_id')
    .in('user_id', emails)
    .not('company_name', 'is', null)
    .limit(1)
    .maybeSingle();

  return companyInfo;
}

export async function getUserProfile(): Promise<UserProfile | null> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.email) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('user_settings')
      .select('*')
      .eq('user_id', localUser.email)
      .maybeSingle();

    if (error) throw error;

    if (!data) {
      const insertPayload: Record<string, unknown> = {
        client_id: localUser.client_id,
        user_id: localUser.email,
        email_address: localUser.email,
        full_name: localUser.name || '',
      };

      const teammateCompany = await findTeammateCompanyInfo(localUser.email);
      if (teammateCompany) {
        if (teammateCompany.company_name) insertPayload.company_name = teammateCompany.company_name;
        if (teammateCompany.company_logo_url) insertPayload.company_logo_url = teammateCompany.company_logo_url;
        if (teammateCompany.company_website) insertPayload.company_website = teammateCompany.company_website;
        if (teammateCompany.country) insertPayload.country = teammateCompany.country;
        if (teammateCompany.company_industry) insertPayload.company_industry = teammateCompany.company_industry;
        if (teammateCompany.icp_config) insertPayload.icp_config = teammateCompany.icp_config;
        if (teammateCompany.daily_call_limit) insertPayload.daily_call_limit = teammateCompany.daily_call_limit;
        if (teammateCompany.daily_lead_generation_limit) insertPayload.daily_lead_generation_limit = teammateCompany.daily_lead_generation_limit;
        if (teammateCompany.company_profile_url) insertPayload.company_profile_url = teammateCompany.company_profile_url;
        if (teammateCompany.company_profile_material_id) insertPayload.company_profile_material_id = teammateCompany.company_profile_material_id;
      }

      const { data: newProfile, error: upsertError } = await supabase
        .from('user_settings')
        .upsert(insertPayload, { onConflict: 'user_id' })
        .select()
        .maybeSingle();

      if (upsertError) throw upsertError;
      return newProfile;
    }

    const updates: Record<string, unknown> = {};

    if (!data.full_name && localUser.name) {
      updates.full_name = localUser.name;
    }

    const missingCompanyFields = !data.company_name || !data.company_logo_url || !data.company_website || !data.country || !data.company_industry || !data.icp_config || !data.daily_call_limit || !data.daily_lead_generation_limit || !data.company_profile_url || !data.company_profile_material_id;
    if (missingCompanyFields) {
      const teammateCompany = await findTeammateCompanyInfo(localUser.email);
      if (teammateCompany) {
        if (teammateCompany.company_name && !data.company_name) updates.company_name = teammateCompany.company_name;
        if (teammateCompany.company_logo_url && !data.company_logo_url) updates.company_logo_url = teammateCompany.company_logo_url;
        if (teammateCompany.company_website && !data.company_website) updates.company_website = teammateCompany.company_website;
        if (teammateCompany.country && !data.country) updates.country = teammateCompany.country;
        if (teammateCompany.company_industry && !data.company_industry) updates.company_industry = teammateCompany.company_industry;
        if (teammateCompany.icp_config && !data.icp_config) updates.icp_config = teammateCompany.icp_config;
        if (teammateCompany.daily_call_limit && !data.daily_call_limit) updates.daily_call_limit = teammateCompany.daily_call_limit;
        if (teammateCompany.daily_lead_generation_limit && !data.daily_lead_generation_limit) updates.daily_lead_generation_limit = teammateCompany.daily_lead_generation_limit;
        if (teammateCompany.company_profile_url && !data.company_profile_url) updates.company_profile_url = teammateCompany.company_profile_url;
        if (teammateCompany.company_profile_material_id && !data.company_profile_material_id) updates.company_profile_material_id = teammateCompany.company_profile_material_id;
      }
    }

    if (Object.keys(updates).length > 0) {
      await supabase
        .from('user_settings')
        .update(updates)
        .eq('user_id', localUser.email);
      return { ...data, ...updates } as UserProfile;
    }

    return data;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    return null;
  }
}

const COMPANY_FIELDS = ['company_name', 'company_website', 'country', 'company_industry', 'company_logo_url', 'company_profile_url', 'company_profile_material_id'] as const;

export async function updateUserProfile(profile: Partial<UserProfile>): Promise<boolean> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.email) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('user_settings')
      .update(profile)
      .eq('user_id', localUser.email);

    if (error) throw error;

    const companyUpdate: Record<string, unknown> = {};
    for (const key of COMPANY_FIELDS) {
      if (key in profile) {
        companyUpdate[key] = profile[key as keyof UserProfile];
      }
    }

    if (Object.keys(companyUpdate).length > 0) {
      const { data: myOrg } = await supabase
        .from('client_users')
        .select('client_id')
        .eq('email', localUser.email)
        .limit(1)
        .maybeSingle();

      if (myOrg?.client_id) {
        const { data: orgMembers } = await supabase
          .from('client_users')
          .select('email')
          .eq('client_id', myOrg.client_id)
          .neq('email', localUser.email);

        if (orgMembers && orgMembers.length > 0) {
          const emails = orgMembers.map(m => m.email);
          await supabase
            .from('user_settings')
            .update(companyUpdate)
            .in('user_id', emails);
        }
      }
    }

    return true;
  } catch (error) {
    console.error('Error updating user profile:', error);
    return false;
  }
}

export async function uploadProfilePicture(file: File): Promise<string | null> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.id || !localUser?.email) throw new Error('Not authenticated');

    const fileExt = file.name.split('.').pop();
    const fileName = `${localUser.id}/profile.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from('profile-pictures')
      .upload(fileName, file, { upsert: true });

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from('profile-pictures')
      .getPublicUrl(fileName);

    await updateUserProfile({ profile_picture_url: publicUrl });

    return publicUrl;
  } catch (error) {
    console.error('Error uploading profile picture:', error);
    return null;
  }
}

export async function uploadCompanyLogo(file: File): Promise<string | null> {
  try {
    const localUser = getLocalUser();
    console.log('Uploading company logo for user:', localUser?.id);

    if (!localUser?.id || !localUser?.email) {
      console.error('Authentication error: Missing user ID or email');
      throw new Error('Not authenticated');
    }

    const fileExt = file.name.split('.').pop();
    const fileName = `${localUser.id}/company-logo.${fileExt}`;

    console.log('Uploading file to storage:', fileName);

    const { error: uploadError } = await supabase.storage
      .from('profile-pictures')
      .upload(fileName, file, { upsert: true });

    if (uploadError) {
      console.error('Storage upload error:', uploadError);
      throw uploadError;
    }

    const { data: { publicUrl } } = supabase.storage
      .from('profile-pictures')
      .getPublicUrl(fileName);

    console.log('Generated public URL:', publicUrl);

    const updateSuccess = await updateUserProfile({ company_logo_url: publicUrl });

    if (!updateSuccess) {
      console.error('Failed to update profile with new logo URL');
    }

    return publicUrl;
  } catch (error) {
    console.error('Error uploading company logo:', error);
    return null;
  }
}

export interface ConnectedEmailAccount {
  id: string;
  display_name: string;
  email: string;
  agent_gender: string | null;
  is_active: boolean;
}

export async function getConnectedEmailAccounts(): Promise<ConnectedEmailAccount[]> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.client_id) return [];

    const { data, error } = await supabase
      .from('email_connections')
      .select('id, display_name, email, agent_gender, is_active')
      .eq('client_id', localUser.client_id)
      .eq('is_active', true);

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching connected email accounts:', error);
    return [];
  }
}

export interface ConnectedWhatsAppAccount {
  id: string;
  agent_name: string;
  phone_number: string;
  agent_gender: string | null;
  voice_notes_allowed: boolean | null;
  voice_note_example: string | null;
  video_messages_allowed: boolean | null;
  is_active: boolean;
}

export async function getConnectedWhatsAppAccounts(): Promise<ConnectedWhatsAppAccount[]> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.client_id) return [];

    const { data, error } = await supabase
      .from('whatsapp_accounts')
      .select('id, agent_name, phone_number, agent_gender, voice_notes_allowed, voice_note_example, video_messages_allowed, is_active')
      .eq('client_id', localUser.client_id)
      .eq('is_active', true)
      .eq('status', 'active');

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching connected WhatsApp accounts:', error);
    return [];
  }
}

export type AgentType = 'call' | 'whatsapp' | 'email' | 'linkedin';

export async function uploadAgentAvatar(agentType: AgentType, file: File): Promise<string | null> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.id || !localUser?.email) throw new Error('Not authenticated');

    const fileExt = file.name.split('.').pop();
    const fileName = `${localUser.id}/${agentType}-avatar.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from('agents-avatars')
      .upload(fileName, file, { upsert: true });

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from('agents-avatars')
      .getPublicUrl(fileName);

    const timestampedUrl = `${publicUrl}?t=${Date.now()}`;

    await upsertAgentSettingsRow(agentType, { avatar_url: timestampedUrl });

    return timestampedUrl;
  } catch (error) {
    console.error('Error uploading agent avatar:', error);
    return null;
  }
}

export async function getAgentSettingsRow(agentType: AgentType): Promise<any | null> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.client_id) return null;

    const { data, error } = await supabase
      .from('agents_settings')
      .select('*')
      .eq('client_id', localUser.client_id)
      .eq('agent_type', agentType)
      .maybeSingle();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching agent settings row:', error);
    return null;
  }
}

export interface VapiAgent {
  vapi_agent_internal_id: string;
  agent_name: string | null;
  vapi_agent_gender: string | null;
  vapi_agent_language: string | null;
}

export async function getVapiAgents(): Promise<VapiAgent[]> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.client_id) return [];

    const { data, error } = await supabase
      .from('vapi_agents')
      .select('vapi_agent_internal_id, agent_name, vapi_agent_gender, vapi_agent_language')
      .eq('client_id', localUser.client_id)
      .order('vapi_agent_gender', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching VAPI agents:', error);
    return [];
  }
}

export async function upsertAgentSettingsRow(agentType: AgentType, fields: Record<string, any>): Promise<boolean> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.client_id || !localUser?.email) throw new Error('Not authenticated');

    const { data: existing } = await supabase
      .from('agents_settings')
      .select('agent_id')
      .eq('client_id', localUser.client_id)
      .eq('agent_type', agentType)
      .maybeSingle();

    if (existing) {
      const { error } = await supabase
        .from('agents_settings')
        .update({ ...fields, updated_at: new Date().toISOString() })
        .eq('agent_id', existing.agent_id);
      if (error) throw error;
    } else {
      const { error } = await supabase
        .from('agents_settings')
        .insert({
          client_id: localUser.client_id,
          user_id: localUser.email,
          agent_type: agentType,
          ...fields,
        })
        .select()
        .maybeSingle();
      if (error && error.code === '23505') {
        const { data: retry } = await supabase
          .from('agents_settings')
          .select('agent_id')
          .eq('client_id', localUser.client_id)
          .eq('agent_type', agentType)
          .maybeSingle();
        if (retry) {
          const { error: updateErr } = await supabase
            .from('agents_settings')
            .update({ ...fields, updated_at: new Date().toISOString() })
            .eq('agent_id', retry.agent_id);
          if (updateErr) throw updateErr;
        } else {
          throw error;
        }
      } else if (error) {
        throw error;
      }
    }

    return true;
  } catch (error) {
    console.error('Error upserting agent settings row:', error);
    return false;
  }
}

async function loadAgentRules(agentType: string): Promise<{ do_rules: { id: string; text: string }[]; dont_rules: { id: string; text: string }[] }> {
  try {
    const row = await getAgentSettingsRow(agentType);
    const rules = row?.agent_rules || {};
    return {
      do_rules: Array.isArray(rules.do) ? rules.do : [],
      dont_rules: Array.isArray(rules.dont) ? rules.dont : [],
    };
  } catch {
    return { do_rules: [], dont_rules: [] };
  }
}

async function getDefaultsWithIdentity(agentType: AgentType): Promise<any> {
  const defaults = getDefaultAgentSettings(agentType);
  try {
    const [row, rules] = await Promise.all([
      getAgentSettingsRow(agentType),
      loadAgentRules(agentType),
    ]);
    const id = row?.agents_identity || {};
    defaults.do_rules = rules.do_rules;
    defaults.dont_rules = rules.dont_rules;

    if (agentType === 'call') {
      if (id.vapi_agent_id) defaults.vapi_agent_id = id.vapi_agent_id;
      if (id.agent_name) defaults.agent_name = id.agent_name;
      if (id.gender) defaults.gender = id.gender;
      if (id.nationality) defaults.nationality = id.nationality;
      if (id.location) defaults.location = id.location;
      if (id.tone) defaults.tone = normalizeToneValue(id.tone);
      if (id.tone_instructions) defaults.tone_instructions = id.tone_instructions;
      if (id.role) defaults.role = id.role;
      if (id.objective) defaults.objective = id.objective;
      if (id.phone_number) defaults.phone_number = id.phone_number;
      if (id.calling_number) defaults.calling_number = id.calling_number;
      if (id.max_calls_per_lead) defaults.max_calls_per_lead = id.max_calls_per_lead;
    } else if (agentType === 'email') {
      if (id.agent_name) defaults.agent_name = id.agent_name;
      if (id.gender) defaults.gender = id.gender;
      if (id.nationality) defaults.nationality = id.nationality;
      if (id.location) defaults.location = id.location;
      if (id.languages) defaults.languages = id.languages;
      if (id.role) defaults.role = id.role;
      if (id.objective) defaults.objective = id.objective;
      if (id.from_email) defaults.from_email = id.from_email;
      if (id.tone) defaults.tone = normalizeToneValue(id.tone);
      if (id.tone_instructions) defaults.tone_instructions = id.tone_instructions;
      if (id.email_layout) defaults.email_layout = id.email_layout;
      if (id.domain) defaults.domain = id.domain;
      if (id.signature) defaults.signature = id.signature;
      if (id.max_emails) defaults.max_emails = id.max_emails;
      if (id.email_connection_id) defaults.email_connection_id = id.email_connection_id;
    } else if (agentType === 'whatsapp') {
      if (id.agent_name) defaults.agent_name = id.agent_name;
      if (id.gender) defaults.gender = id.gender;
      if (id.nationality) defaults.nationality = id.nationality;
      if (id.location) defaults.location = id.location;
      if (id.languages) defaults.languages = id.languages;
      if (id.tone) defaults.tone = normalizeToneValue(id.tone);
      if (id.tone_instructions) defaults.tone_instructions = id.tone_instructions;
      if (id.role) defaults.role = id.role;
      if (id.objective) defaults.objective = id.objective;
      if (id.phone_number) defaults.phone_number = id.phone_number;
      if (id.max_follow_ups) defaults.max_follow_ups = id.max_follow_ups;
      if (id.whatsapp_account_id) defaults.whatsapp_account_id = id.whatsapp_account_id;
      if (id.voice_notes_allowed != null) defaults.voice_notes_allowed = id.voice_notes_allowed;
      if (id.voice_note_example) defaults.voice_note_example = id.voice_note_example;
    } else if (agentType === 'linkedin') {
      if (id.agent_persona) defaults.agent_persona = id.agent_persona;
      if (id.languages) defaults.languages = id.languages;
      if (id.tone) defaults.tone = normalizeToneValue(id.tone);
      if (id.tone_instructions) defaults.tone_instructions = id.tone_instructions;
      if (id.role) defaults.role = id.role;
      if (id.objective) defaults.objective = id.objective;
      if (id.max_messages) defaults.max_messages = id.max_messages;
      if (id.linkedin_account_id) defaults.linkedin_account_id = id.linkedin_account_id;
      if (id.location) defaults.location = id.location;
      if (id.voice_notes_allowed != null) defaults.voice_notes_allowed = id.voice_notes_allowed;
      if (id.voice_note_example) defaults.voice_note_example = id.voice_note_example;
    }
  } catch {
    // fall through with raw defaults
  }
  return defaults;
}

export async function getAgentSettings(agentType: 'call' | 'whatsapp' | 'email' | 'linkedin'): Promise<any> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.client_id) throw new Error('Not authenticated');

    if (agentType === 'email') {
      const { data: emailRows } = await supabase
        .from('email_templates')
        .select('*')
        .eq('client_id', localUser.client_id)
        .in('status', ['draft', 'approved'])
        .neq('source', 'system')
        .order('created_at', { ascending: false });

      const defaults = (getDefaultAgentSettings('email') as EmailAgentSettings).templates!;
      const templates: any = { ...defaults };

      if (emailRows) {
        const seen = new Set<string>();
        for (const row of emailRows) {
          const key = row.template_key as EmailTemplateKey;
          if (!seen.has(key) && EMAIL_TEMPLATE_DEFINITIONS.some(t => t.key === key)) {
            seen.add(key);
            templates[key] = {
              subject: row.subject_text || '',
              body: row.body_text || '',
            };
          }
        }
      }

      const [emailRules, emailRow] = await Promise.all([
        loadAgentRules('email'),
        getAgentSettingsRow('email'),
      ]);
      const emailId = emailRow?.agents_identity || {};
      return {
        mode: 'preset',
        templates: templates as EmailTemplates,
        domain: emailId.domain || '',
        agent_name: emailId.agent_name || '',
        gender: emailId.gender || 'female',
        nationality: emailId.nationality || [],
        location: emailId.location || '',
        languages: emailId.languages || ['english'],
        role: emailId.role || 'Sales Consultant',
        objective: emailId.objective || 'Book a meeting',
        from_email: emailId.from_email || '',
        tone: normalizeToneValue(emailId.tone || 'professional'),
        tone_instructions: emailId.tone_instructions || '',
        email_layout: emailId.email_layout || 'standard',
        signature: emailId.signature || '',
        max_emails: emailId.max_emails || 4,
        delay_days: 3,
        do_rules: emailRules.do_rules,
        dont_rules: emailRules.dont_rules,
        email_connection_id: emailId.email_connection_id || '',
      } as EmailAgentSettings;
    }

    if (agentType === 'whatsapp') {
      const { data: waRows } = await supabase
        .from('whatsapp_templates')
        .select('*')
        .eq('client_id', localUser.client_id)
        .in('status', ['draft', 'approved', 'pending_review'])
        .neq('source', 'system')
        .order('created_at', { ascending: false });

      const defaults = (getDefaultAgentSettings('whatsapp') as WhatsAppAgentSettings).templates!;
      const templates: any = { ...defaults };

      if (waRows) {
        const seen = new Set<string>();
        for (const row of waRows) {
          const key = row.template_key as WhatsAppTemplateKey;
          if (!seen.has(key) && WA_TEMPLATE_DEFINITIONS.some(t => t.key === key)) {
            seen.add(key);
            templates[key] = {
              text: row.submitted_text || '',
              button_text: row.submitted_button_text || '',
              button_url: row.submitted_button_url || '',
            };
          }
        }
      }

      const [waRules, waRow] = await Promise.all([
        loadAgentRules('whatsapp'),
        getAgentSettingsRow('whatsapp'),
      ]);
      const waId = waRow?.agents_identity || {};
      return {
        mode: 'template',
        templates: templates as WhatsAppTemplates,
        agent_name: waId.agent_name || '',
        gender: waId.gender || 'female',
        nationality: waId.nationality || [],
        location: waId.location || '',
        languages: waId.languages || ['english'],
        tone: normalizeToneValue(waId.tone || 'friendly'),
        tone_instructions: waId.tone_instructions || '',
        role: waId.role || 'Sales Consultant',
        objective: waId.objective || 'Book a meeting',
        phone_number: waId.phone_number || '',
        whatsapp_number: '',
        whatsapp_account_id: waId.whatsapp_account_id || '',
        voice_notes_allowed: waId.voice_notes_allowed ?? false,
        voice_note_example: waId.voice_note_example || '',
        max_follow_ups: waId.max_follow_ups || 3,
        delay_hours: 48,
        do_rules: waRules.do_rules,
        dont_rules: waRules.dont_rules,
      } as WhatsAppAgentSettings;
    }

    if (agentType === 'linkedin') {
      const { data: liRows } = await supabase
        .from('linkedin_templates')
        .select('*')
        .eq('client_id', localUser.client_id)
        .in('status', ['draft', 'approved'])
        .neq('source', 'system')
        .order('created_at', { ascending: false });

      const defaults = getDefaultAgentSettings(agentType) as LinkedInAgentSettings;
      const templates: LinkedInTemplates = { ...defaults.templates! };

      if (liRows) {
        const seen = new Set<string>();
        for (const row of liRows) {
          const key = row.template_key as LinkedInTemplateKey;
          if (!seen.has(key) && LI_TEMPLATE_DEFINITIONS.some(t => t.key === key)) {
            seen.add(key);
            templates[key] = { text: row.message_text || '' };
          }
        }
      }

      const [liRules, liRow] = await Promise.all([
        loadAgentRules('linkedin'),
        getAgentSettingsRow('linkedin'),
      ]);
      const liId = liRow?.agents_identity || {};
      return {
        mode: 'template',
        templates,
        agent_persona: liId.agent_persona || 'Senior Sales Executive',
        languages: liId.languages || ['english'],
        tone: normalizeToneValue(liId.tone || 'friendly'),
        tone_instructions: liId.tone_instructions || '',
        role: liId.role || 'Sales Consultant',
        objective: liId.objective || 'Book a meeting',
        max_messages: liId.max_messages || LI_TEMPLATE_DEFINITIONS.length,
        delay_days: 3,
        linkedin_account_id: liId.linkedin_account_id || '',
        location: liId.location || '',
        voice_notes_allowed: liId.voice_notes_allowed ?? false,
        voice_note_example: liId.voice_note_example || '',
        do_rules: liRules.do_rules,
        dont_rules: liRules.dont_rules,
      } as LinkedInAgentSettings;
    }

    if (agentType === 'call') {
      const { data: callRow } = await supabase
        .from('call_scripts')
        .select('*')
        .eq('client_id', localUser.client_id)
        .in('status', ['draft', 'approved'])
        .neq('source', 'system')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!callRow?.script_data) {
        return getDefaultsWithIdentity(agentType);
      }

      const migrated = migrateCallSettings(callRow.script_data);
      const [callRules, callSettingsRow] = await Promise.all([
        loadAgentRules('call'),
        getAgentSettingsRow('call'),
      ]);
      const callId = callSettingsRow?.agents_identity || {};
      migrated.vapi_agent_id = callId.vapi_agent_id || '';
      migrated.agent_name = callId.agent_name || migrated.agent_name || '';
      migrated.gender = callId.gender || migrated.gender || 'male';
      migrated.nationality = callId.nationality || migrated.nationality || [];
      migrated.location = callId.location || migrated.location || '';
      migrated.tone = normalizeToneValue(callId.tone || migrated.tone || 'friendly');
      migrated.tone_instructions = callId.tone_instructions || '';
      migrated.role = callId.role || migrated.role || 'Sales Consultant';
      migrated.objective = callId.objective || migrated.objective || 'Book a meeting';
      migrated.phone_number = callId.phone_number || migrated.phone_number || '';
      migrated.phone_number_id = callId.phone_number_id || migrated.phone_number_id || '';
      migrated.max_calls_per_lead = callId.max_calls_per_lead || migrated.max_calls_per_lead || 3;
      migrated.do_rules = callRules.do_rules;
      migrated.dont_rules = callRules.dont_rules;
      return migrated;
    }

    return getDefaultAgentSettings(agentType);
  } catch (error) {
    console.error(`Error fetching ${agentType} agent settings:`, error);
    try {
      return await getDefaultsWithIdentity(agentType);
    } catch {
      return getDefaultAgentSettings(agentType);
    }
  }
}

function migrateCallSettings(settings: any): any {
  const migratedSettings = { ...settings };
  const defaults = getDefaultAgentSettings('call');

  if (!migratedSettings.objection_handling || !Array.isArray(migratedSettings.objection_handling)) {
    if (migratedSettings.script_1?.objection_handling) {
      delete migratedSettings.script_1.objection_handling;
    }
    if (migratedSettings.script_2?.objection_handling) {
      delete migratedSettings.script_2.objection_handling;
    }
    migratedSettings.objection_handling = defaults.objection_handling;
  }

  if (migratedSettings.script_1 && 'greeting' in migratedSettings.script_1 && !('greetings' in migratedSettings.script_1)) {
    const old = migratedSettings.script_1;
    migratedSettings.script_1 = {
      greetings: old.greeting || '',
      introduction: '',
      why_we_are_calling: old.value_statement || '',
      elaboration_credibility: '',
      engagement: '',
      qualification: old.qualifying_questions || '',
      next_steps: '',
      booking: '',
      closing: old.closing || '',
    };
  }
  if (!migratedSettings.script_1) {
    migratedSettings.script_1 = defaults.script_1;
  }

  if (migratedSettings.script_2 && 'greeting' in migratedSettings.script_2 && !('greetings' in migratedSettings.script_2)) {
    const old = migratedSettings.script_2;
    migratedSettings.script_2 = {
      greetings: old.greeting || '',
      introduction: '',
      why_we_are_calling_again: old.value_statement || '',
      engagement: '',
      qualification_if_not_done: old.qualifying_questions || '',
      next_steps: '',
      booking: '',
      closing: old.closing || '',
    };
  }
  if (!migratedSettings.script_2) {
    migratedSettings.script_2 = defaults.script_2;
  }

  if (migratedSettings.max_calls_per_contact !== undefined && migratedSettings.max_calls_per_lead === undefined) {
    migratedSettings.max_calls_per_lead = migratedSettings.max_calls_per_contact;
    delete migratedSettings.max_calls_per_contact;
  }

  if (migratedSettings.nationality === undefined) migratedSettings.nationality = [];
  if (migratedSettings.location === undefined) migratedSettings.location = '';
  if (migratedSettings.tone === undefined) migratedSettings.tone = 'friendly_casual';
  else migratedSettings.tone = normalizeToneValue(migratedSettings.tone);
  if (migratedSettings.role === undefined) migratedSettings.role = 'Sales Consultant';
  if (migratedSettings.objective === undefined) migratedSettings.objective = 'Book a meeting';
  if (migratedSettings.max_calls_per_lead === undefined) migratedSettings.max_calls_per_lead = 3;
  if (migratedSettings.do_rules === undefined) migratedSettings.do_rules = [];
  if (migratedSettings.dont_rules === undefined) migratedSettings.dont_rules = [];

  return migratedSettings;
}

function getDefaultAgentSettings(agentType: string): any {
  switch (agentType) {
    case 'call':
      return {
        script_1: {
          greetings: 'Hi {First_Name}, this is {Agent_Name} calling from {Company}. How are you today?',
          introduction: 'I know you\'re busy, so I\'ll be brief. I noticed that {Company_Name} is in the {Industry} space, and I wanted to reach out because we help companies like yours streamline their sales processes.',
          why_we_are_calling: 'The reason I\'m calling is that many companies in {Industry} are losing qualified leads because their outreach isn\'t consistent. We\'ve developed an AI-powered platform that helps teams like yours automate their follow-ups and increase their pipeline without adding headcount.',
          elaboration_credibility: 'We work with companies in your industry to help them increase qualified leads by an average of 40% while reducing manual workload. For example, we recently worked with a company similar to yours that was struggling with follow-up consistency. Within 3 months, they saw a 2x increase in meeting bookings.',
          engagement: 'I\'m curious - are you finding that your team has enough time to follow up with every lead, or are some falling through the cracks?\n\nWhat does your current outreach process look like day-to-day?',
          qualification: 'Can I ask - what are you currently using for lead generation and outreach?\n\nHow many leads is your team typically working with each month?\n\nWhat\'s your biggest challenge right now when it comes to sales productivity?',
          next_steps: 'Based on what you\'ve shared, I think we could really help {Company_Name}. What I\'d like to suggest is a quick 15-minute demo where I can show you exactly how this would work for your specific situation.',
          booking: 'I have availability Tuesday at 2pm or Thursday at 10am - which works better for you?\n\nI\'ll send over a calendar invite right away with a brief agenda so you know exactly what to expect.',
          closing: 'Great, I\'ll get that booked in. Really looking forward to showing you how we can help {Company_Name}. Have a wonderful rest of your day, {First_Name}!',
        },
        script_2: {
          greetings: 'Hi {First_Name}, it\'s {Agent_Name} again from {Company}. Do you have a quick minute?',
          introduction: 'We spoke about a week ago about improving {Company_Name}\'s sales process, and I wanted to follow up on our conversation.',
          why_we_are_calling_again: 'I wanted to follow up because I\'ve been thinking about what you mentioned regarding your current challenges. We actually just helped another client in {Industry} overcome a similar situation, and I thought it would be worth sharing.',
          engagement: 'Have you had a chance to think about our last conversation? I\'d love to hear if anything has changed on your end since we last spoke.',
          qualification_if_not_done: 'What would need to happen for this to become a priority for {Company_Name}?\n\nAre there any concerns or questions I can address that would help you make a decision?',
          next_steps: 'I don\'t want to keep calling if the timing isn\'t right. How about we schedule one quick 15-minute call where I can show you the platform, and then you can make an informed decision? Does that sound fair?',
          booking: 'Would Wednesday at 3pm or Friday at 11am work for you?\n\nI\'ll send a calendar invite with everything you need right after we hang up.',
          closing: 'Perfect, I\'ll get that set up. Thanks for your time today, {First_Name}. Looking forward to our meeting!',
        },
        objection_handling: [
          {
            id: '1',
            title: 'I\'m too busy right now',
            response: 'I completely understand you\'re busy - that\'s exactly why I\'m calling. Our solution is designed to save you time, not take more of it. How about I send you some information you can review when it\'s convenient, and we can schedule a brief 15-minute call next week? Would Tuesday or Thursday work better for you?',
            category: 'timing',
            order: 1,
          },
          {
            id: '2',
            title: 'We don\'t have the budget',
            response: 'I appreciate you being upfront about budget concerns. What if we start with a pilot program to demonstrate ROI first? Many of our clients found that helpful, and it typically pays for itself within the first month. Would exploring that option make sense?',
            category: 'budget',
            order: 2,
          },
          {
            id: '3',
            title: 'We\'re already using another solution',
            response: 'That\'s great that you\'re already invested in a solution. I\'m curious - what\'s working well with your current setup, and is there anything you wish it did better? Many of our clients use us alongside their existing tools, and we often integrate seamlessly.',
            category: 'competition',
            order: 3,
          },
          {
            id: '4',
            title: 'Just send me some information',
            response: 'I\'d be happy to send information your way. To make sure I send you exactly what\'s relevant, can I ask - what specific aspect of {your product/service} would be most valuable for {Company_Name}? That way I can tailor the materials to your needs.',
            category: 'other',
            order: 4,
          },
          {
            id: '5',
            title: 'I need to talk to my team first',
            response: 'That makes complete sense - this sounds like a team decision. Who else would typically be involved in evaluating something like this? I\'m happy to set up a call with everyone at once, or provide materials you can share internally. What would be most helpful?',
            category: 'authority',
            order: 5,
          },
          {
            id: '6',
            title: 'Call me back in [timeframe]',
            response: 'Absolutely, I\'d be happy to follow up then. Just so I\'m prepared for our next conversation - what will have changed by that time? Is there something specific happening, or would you just like some time to think it over?',
            category: 'timing',
            order: 6,
          },
          {
            id: '7',
            title: 'We\'re not interested',
            response: 'I appreciate your directness. Can I ask - is it that the timing isn\'t right, or is there something specific about what we offer that doesn\'t fit? I want to make sure I\'m not wasting your time, but I also want to understand if there\'s a way we could be helpful in the future.',
            category: 'need',
            order: 7,
          },
          {
            id: '8',
            title: 'How did you get my number?',
            response: 'That\'s a fair question. I found your information through {source - LinkedIn/company website/industry directory}. I specifically reached out because {Company_Name} fits the profile of companies we\'ve helped successfully. Is this a good time to chat, or would you prefer I call back later?',
            category: 'other',
            order: 8,
          },
          {
            id: '9',
            title: 'We\'re happy with our current process',
            response: 'That\'s wonderful to hear! I\'m always curious to learn from companies that have their processes dialed in. What\'s working particularly well for you? And if there was one thing you could improve or automate, what would it be?',
            category: 'need',
            order: 9,
          },
          {
            id: '10',
            title: 'What\'s this regarding?',
            response: 'Thanks for asking. I\'m calling because we help companies in {Industry} like {Company_Name} streamline their sales processes and increase qualified leads. I noticed {specific trigger/observation}, and thought there might be an opportunity to help. Do you have a quick minute to discuss?',
            category: 'other',
            order: 10,
          },
          {
            id: '11',
            title: 'This isn\'t a priority right now',
            response: 'I understand - priorities are constantly shifting. Can I ask what you\'re focusing on instead? I\'m curious if there\'s any overlap with what we do. And when do you think this might become more of a priority for {Company_Name}?',
            category: 'timing',
            order: 11,
          },
          {
            id: '12',
            title: 'We just signed a contract with a competitor',
            response: 'Congratulations on making a decision - that\'s a big step. I\'m curious, how long is your contract for? Even if the timing isn\'t right now, I\'d love to stay in touch and perhaps do a comparison down the road. Would it be okay if I check back in closer to your renewal date?',
            category: 'competition',
            order: 12,
          },
        ],
        agent_name: 'Sarah',
        gender: 'female',
        nationality: [],
        location: '',
        tone: 'friendly_casual',
        role: 'Sales Consultant',
        objective: 'Book a meeting',
        phone_number: '+1 (555) 100-1000',
        calling_number: '+1 (555) 000-0000',
        max_calls_per_lead: 3,
        do_rules: [],
        dont_rules: [],
      };
    case 'whatsapp':
      return {
        mode: 'template',
        templates: {
          wa_outreach_first: {
            text: 'Hi {First_Name}, I\'m {Agent_Name} from {Company}. I came across {Company_Name} and was impressed by your work in {Industry}.\n\nWe help companies like yours automate their sales outreach and increase qualified leads by 40%+ on average. I thought this might be relevant for your team.\n\nWould you be open to a quick chat about how we could help {Company_Name} scale your sales efforts?',
          },
          wa_outreach_fu_cold_1: {
            text: 'Hi {First_Name}, just following up on my previous message about helping {Company_Name} increase qualified leads.\n\nI wanted to share that we recently worked with a similar company in {Industry} who saw a 2x increase in meeting bookings within their first quarter.\n\nWould you be interested in learning how they did it? Happy to share more details!',
          },
          wa_outreach_fu_cold_2: {
            text: 'Hey {First_Name}, I hope you\'re well! I wanted to reach out one more time because I genuinely believe we could help {Company_Name} achieve your growth goals.\n\nAre you open to a brief 15-minute call next week? I\'d love to understand your current challenges and see if we\'re a fit.',
          },
          wa_outreach_fu_cold_3: {
            text: 'Hi {First_Name}, I understand you\'re probably busy, so I\'ll keep this short.\n\nIf the timing isn\'t right now, no problem at all. Would it make sense for me to check back in a couple of months?\n\nOr if you\'re interested but need more information first, I\'m happy to send over some resources you can review at your convenience.',
          },
          wa_outreach_fu_warm_1: {
            text: 'Hi {First_Name}, great chatting with you! I wanted to follow up on what we discussed about helping {Company_Name} scale your outreach.\n\nI put together a quick overview of how we\'ve helped similar companies in {Industry} achieve a 40%+ increase in qualified leads. Would you like me to share it?\n\nAlso, are you still available for that call we talked about?',
          },
          wa_outreach_fu_warm_2: {
            text: 'Hi {First_Name}, just circling back on our conversation. I know things can get hectic, so I wanted to make sure this stays on your radar.\n\nWe\'ve had some exciting results recently with companies in {Industry} - one client booked 3x more meetings in their first month. I\'d love to show you how we could do the same for {Company_Name}.\n\nWould a quick 15-minute call work for you this week?',
          },
          wa_outreach_fu_warm_3: {
            text: 'Hi {First_Name}, I hope you\'ve had a chance to think about what we discussed. I completely understand if the timing isn\'t perfect right now.\n\nIf you\'re still interested, I\'m happy to set up a brief demo whenever it suits you. If not, no worries at all - I\'ll check back in a couple of months.\n\nJust let me know what works best for you!',
          },
          wa_inbound_first: {
            text: 'Hi {First_Name}! Thanks for reaching out to {Company}. I\'m {Agent_Name} and I\'d love to help you with what you\'re looking for.\n\nCould you tell me a bit more about what {Company_Name} needs? That way I can point you in the right direction and see how we can best assist.',
          },
          wa_inbound_fu_1: {
            text: 'Hi {First_Name}, just following up on your inquiry to {Company}. I wanted to make sure you got everything you needed.\n\nIf you have any questions or would like to schedule a quick call, I\'m happy to help. What works best for you?',
          },
          wa_inbound_fu_2: {
            text: 'Hi {First_Name}, circling back one more time on your inquiry. I\'d hate for you to miss out on what we discussed.\n\nIf the timing isn\'t right, no worries at all. Just let me know and I can follow up later when it makes more sense for {Company_Name}.',
          },
          wa_inbound_lm_first: {
            text: 'Hi {First_Name}! Thanks for downloading our resource from {Company}. I\'m {Agent_Name} and I wanted to make sure you got the most out of it.\n\nHappy to answer any questions or walk you through how others at companies like {Company_Name} have put it to use. What caught your attention?',
          },
          wa_inbound_lm_fu_1: {
            text: 'Hi {First_Name}, did you get a chance to look through the resource you downloaded? I\'d love to hear your thoughts.\n\nIf anything wasn\'t clear or you\'d like to go deeper on a specific point, just let me know and I\'m happy to help.',
          },
          wa_inbound_lm_fu_2: {
            text: 'Hi {First_Name}, wanted to share how other teams have turned that resource into real results.\n\nIf you\'re open to it, I can show you what that could look like for {Company_Name}. No pressure at all - just let me know if it\'s helpful.',
          },
          wa_other_info_request: {
            text: 'Hi {First_Name}, thanks for your interest! As requested, here\'s more detail on how {Company} can help {Company_Name}.\n\nI\'ve put together some key points that are most relevant to your situation. Let me know if you\'d like to dive deeper on any of these.',
          },
          wa_other_meeting_confirmation: {
            text: 'Hi {First_Name}, your meeting with {Agent_Name} is confirmed for {Meeting_Date} at {Meeting_Time}.\n\nLooking forward to our conversation! If anything comes up, feel free to reach out.',
          },
          wa_other_meeting_remind_24: {
            text: 'Hi {First_Name}, just a friendly reminder about our meeting tomorrow at {Meeting_Time}.\n\nLooking forward to connecting with you!',
          },
          wa_other_meeting_remind_4: {
            text: 'Hi {First_Name}, looking forward to our call in a few hours at {Meeting_Time}.\n\nLet me know if you need to adjust anything.',
          },
          wa_other_meeting_remind_30min: {
            text: 'Hi {First_Name}, we\'re meeting in 30 minutes at {Meeting_Time}. See you soon!',
          },
          wa_other_rescheduled_meeting: {
            text: 'Hi {First_Name}, your meeting has been rescheduled to {Meeting_Date} at {Meeting_Time}.\n\nPlease let me know if this new time works for you. Happy to find an alternative if needed.',
          },
        } as WhatsAppTemplates,
        agent_name: 'Sarah',
        gender: 'female',
        nationality: [],
        location: '',
        tone: 'friendly_casual',
        role: 'Sales Consultant',
        objective: 'Book a meeting',
        phone_number: '+1 (555) 100-1000',
        whatsapp_number: '+1 (555) 000-0000',
        max_follow_ups: 3,
        delay_hours: 48,
        do_rules: [],
        dont_rules: [],
      };
    case 'email':
      return {
        mode: 'preset',
        templates: {
          email_outreach_pitch: {
            subject: 'Quick question about {Company_Name}\'s growth',
            body: 'Hi {First_Name},\n\nI came across {Company_Name} and noticed you\'re in the {Industry} space. I\'m reaching out because we help companies like yours increase qualified leads and streamline sales processes.\n\nWe recently worked with a similar company that saw a 40% increase in qualified pipeline within their first quarter using our AI-powered outreach platform.\n\nWould you be open to a 15-minute call next week to discuss how we might be able to help {Company_Name} achieve similar results?\n\nLet me know what works for your schedule.',
          },
          email_outreach_fu_1: {
            subject: 'Following up - {Company_Name} + {Company} partnership',
            body: 'Hi {First_Name},\n\nI wanted to follow up on my previous email about helping {Company_Name} scale your sales efforts.\n\nI thought you might find this relevant: we recently helped a company in {Industry} increase their meeting bookings by 2x while reducing manual outreach time by 60%.\n\nI\'d love to explore whether we could deliver similar results for your team. Are you available for a brief call this week?\n\nBest regards,',
          },
          email_outreach_fu_2: {
            subject: 'Thought you\'d find this interesting - {Company_Name}',
            body: 'Hi {First_Name},\n\nI hope this email finds you well. I wanted to share something that might be valuable for {Company_Name}.\n\nWe recently published a case study showing how companies in {Industry} are using AI to scale their outreach without sacrificing personalization. The results have been remarkable - one client saw a 50% increase in response rates.\n\nIf this resonates with challenges you\'re facing, I\'d be happy to jump on a quick call to discuss how we could help {Company_Name} achieve similar results.\n\nWhat does your calendar look like next week?',
          },
          email_outreach_fu_3: {
            subject: 'Last note about {Company_Name}\'s sales growth',
            body: 'Hi {First_Name},\n\nI know you\'re busy, so I\'ll keep this brief.\n\nI\'ve reached out a few times about helping {Company_Name} scale your sales efforts through AI-powered outreach. I don\'t want to be a pest, but I genuinely believe we could add value to your team.\n\nIf now isn\'t the right time, I completely understand. Would it make sense for me to check back in a few months?\n\nJust let me know what works best for you.\n\nBest regards,',
          },
          email_outreach_fu_4: {
            subject: 'Closing the loop - {Company_Name}',
            body: 'Hi {First_Name},\n\nThis will be my last email on this topic. I\'ve reached out a few times about how {Company} could help {Company_Name} with your sales efforts.\n\nIf you\'d ever like to revisit the conversation, I\'m always happy to chat. Wishing you and the team all the best.\n\nBest regards,',
          },
          email_inbound_first: {
            subject: 'Thanks for reaching out - {Company_Name} + {Company}',
            body: 'Hi {First_Name},\n\nThank you for your interest in {Company}! I\'m excited to learn more about what {Company_Name} is looking for.\n\nI\'d love to understand your current challenges and goals so I can share the most relevant information about how we can help. Would you be open to a quick 15-minute call this week?\n\nLooking forward to connecting!',
          },
          email_inbound_fu_1: {
            subject: 'Following up on your inquiry - {Company_Name}',
            body: 'Hi {First_Name},\n\nJust following up on your recent inquiry. I wanted to make sure you have everything you need.\n\nWe\'ve helped companies similar to {Company_Name} in {Industry} achieve significant results, and I\'d love to share how we might be able to do the same for you.\n\nWould a brief call work for you this week? Happy to work around your schedule.',
          },
          email_inbound_fu_2: {
            subject: 'Still interested? - {Company_Name} + {Company}',
            body: 'Hi {First_Name},\n\nI wanted to circle back on your inquiry one more time. I understand things get busy, so no pressure at all.\n\nIf you\'re still interested in exploring how {Company} can help {Company_Name}, I\'m here whenever the timing is right. Feel free to reply or book a time directly.\n\nWishing you all the best!',
          },
          email_inbound_lm_first: {
            subject: 'Here\'s your download - {Company}',
            body: 'Hi {First_Name},\n\nThanks for downloading our resource! I hope you find it valuable.\n\nAs you go through it, feel free to reach out if any questions come up. We\'ve helped companies in {Industry} put these ideas into action, and I\'d be happy to share what\'s worked for teams like {Company_Name}.\n\nEnjoy the read!',
          },
          email_inbound_lm_fu_1: {
            subject: 'Did you get a chance to review it? - {Company}',
            body: 'Hi {First_Name},\n\nJust checking in to see if you had a chance to look through the resource I sent over.\n\nIf anything stood out or you\'d like to dig deeper into how it applies to {Company_Name}, I\'d be glad to walk you through it on a quick call.\n\nNo rush at all - just let me know what works.',
          },
          email_inbound_lm_fu_2: {
            subject: 'Want to take the next step? - {Company}',
            body: 'Hi {First_Name},\n\nI hope the resource was helpful! If you\'re exploring ways to get more out of these ideas, I\'d love to show you how {Company} can help {Company_Name} put them into practice.\n\nWould a brief 15-minute call make sense? Happy to work around your schedule.\n\nEither way, wishing you and the team all the best!',
          },
          email_other_info_request: {
            subject: 'As requested - more info about {Company}',
            body: 'Hi {First_Name},\n\nThanks for your interest! As requested, here\'s more detail on how {Company} can help {Company_Name}.\n\nWe specialize in helping companies in {Industry} automate their sales outreach while maintaining a personal touch. Our clients typically see a 40%+ increase in qualified leads within the first quarter.\n\nI\'ve attached some additional resources for your review. Let me know if you have any questions or would like to schedule a quick call to discuss further.',
          },
          email_other_meeting_confirmation: {
            subject: 'Meeting confirmed - {Company_Name} + {Company}',
            body: 'Hi {First_Name},\n\nYour meeting with {Agent_Name} is confirmed for {Meeting_Date} at {Meeting_Time}.\n\nLooking forward to our conversation! If anything comes up, feel free to reach out and we can reschedule.',
          },
          email_other_meeting_remind_24: {
            subject: 'Reminder: Our meeting tomorrow',
            body: 'Hi {First_Name},\n\nJust a friendly reminder about our meeting tomorrow at {Meeting_Time}.\n\nLooking forward to connecting with you!',
          },
          email_other_meeting_remind_4: {
            subject: 'See you in a few hours!',
            body: 'Hi {First_Name},\n\nLooking forward to our call in a few hours at {Meeting_Time}.\n\nLet me know if you need to adjust anything.',
          },
          email_other_meeting_remind_30min: {
            subject: 'Starting in 30 minutes',
            body: 'Hi {First_Name},\n\nWe\'re meeting in 30 minutes at {Meeting_Time}. See you soon!',
          },
          email_other_rescheduled_meeting: {
            subject: 'Meeting rescheduled - new time confirmed',
            body: 'Hi {First_Name},\n\nYour meeting has been rescheduled to {Meeting_Date} at {Meeting_Time}.\n\nPlease let me know if this new time works for you. Happy to find an alternative if needed.',
          },
        } as EmailTemplates,
        agent_name: 'Sarah',
        gender: 'female',
        nationality: [],
        location: '',
        role: 'Sales Consultant',
        objective: 'Book a meeting',
        from_email: 'sales@company.com',
        tone: 'professional',
        email_layout: 'standard',
        signature: 'Best regards,\nSarah Smith\nSenior Sales Executive\n{Company}\nsales@company.com | +1 (555) 000-0000',
        max_emails: 4,
        delay_days: 3,
        do_rules: [],
        dont_rules: [],
      };
    case 'linkedin':
      return {
        mode: 'template',
        templates: {
          li_outreach_connection: { text: 'Hi {First_Name}, I noticed we both work in {Industry}. I\'d love to connect and exchange insights about sales automation and growth strategies!' },
          li_outreach_intro: { text: 'Hi {First_Name}, thanks for connecting!\n\nI work with companies like {Company_Name} to help them scale their sales efforts through AI-powered outreach. We\'ve helped similar companies in {Industry} increase qualified leads by 40%+ while reducing manual work.\n\nI\'d love to learn more about your current sales process and see if there are ways we could help. Would you be open to a brief call next week?' },
          li_outreach_pitch: { text: 'Hi {First_Name}, I wanted to share something relevant to {Company_Name}.\n\nWe\'ve developed a solution that helps companies in {Industry} automate their outreach while keeping it personal. Our clients typically see a 40-60% increase in qualified meetings within the first month.\n\nHere\'s a quick case study that might resonate: [link]\n\nWould you be open to a 15-minute call to explore if this could work for your team?' },
          li_outreach_fu_1: { text: 'Hi {First_Name}, I wanted to follow up on my previous message.\n\nI thought you might find this relevant - we recently worked with a company in {Industry} that increased their pipeline by 50% using our platform. Here\'s a quick overview: [link]\n\nIf you\'re interested, I\'d be happy to show you how this could work for {Company_Name}. Are you available for a quick 15-minute call this week?' },
          li_outreach_fu_2: { text: 'Hey {First_Name}, reaching out one more time.\n\nI know your inbox is busy, so I\'ll keep this short. We\'re helping companies in {Industry} book 3x more meetings with less manual effort.\n\nIf you\'d like to see a quick demo, I\'m happy to set one up. Otherwise, no worries at all!' },
          li_outreach_fu_3: { text: 'Hey {First_Name}, I hope you\'re doing well!\n\nI don\'t want to keep bothering you, but I genuinely think there could be value in connecting. We\'ve had great success helping companies in {Industry} scale their sales efforts efficiently.\n\nIf the timing isn\'t right, I completely understand. Would it make sense to revisit this in a few months? Or if you\'d like to learn more first, I\'m happy to share some resources.' },
        } as LinkedInTemplates,
        agent_persona: 'Senior Sales Executive',
        tone: 'friendly_casual',
        role: 'Sales Consultant',
        objective: 'Book a meeting',
        location: '',
        voice_notes_allowed: false,
        voice_note_example: '',
        max_messages: 6,
        delay_days: 3,
        do_rules: [],
        dont_rules: [],
      };
    default:
      return {};
  }
}

export async function getLinkedInAccounts(): Promise<LinkedInConnection[]> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.client_id) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('linkedin_accounts')
      .select('*')
      .eq('client_id', localUser.client_id)
      .order('is_active', { ascending: false })
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching LinkedIn accounts:', error);
    return [];
  }
}

export async function getLinkedInConnection(): Promise<LinkedInConnection | null> {
  const accounts = await getLinkedInAccounts();
  return accounts.length > 0 ? accounts[0] : null;
}

export async function connectLinkedIn(): Promise<{ success: boolean; url?: string; error?: string }> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.email || !localUser?.client_id) throw new Error('Not authenticated');

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    const response = await fetch(`${supabaseUrl}/functions/v1/linkedin-connect`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`,
      },
      body: JSON.stringify({
        client_id: localUser.client_id,
        user_id: localUser.email,
      }),
    });

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error initiating LinkedIn connection:', error);
    return { success: false, error: 'Failed to start LinkedIn connection' };
  }
}

export async function disconnectLinkedIn(accountId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('linkedin_accounts')
      .update({ is_active: false, updated_at: new Date().toISOString() })
      .eq('id', accountId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error disconnecting LinkedIn:', error);
    return false;
  }
}

export async function deleteLinkedInAccount(accountId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('linkedin_accounts')
      .delete()
      .eq('id', accountId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting LinkedIn account:', error);
    return false;
  }
}

export async function getWaTemplateSubmissions(): Promise<{ submissions: Record<string, WaTemplateSubmission>; approvedKeys: string[] }> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.client_id) return { submissions: {}, approvedKeys: [] };

    const [pendingResult, approvedResult] = await Promise.all([
      supabase
        .from('whatsapp_templates')
        .select('*')
        .eq('client_id', localUser.client_id)
        .eq('user_id', localUser.email)
        .in('status', ['pending_review', 'rejected'])
        .order('created_at', { ascending: false }),
      supabase
        .from('whatsapp_templates')
        .select('template_key')
        .eq('client_id', localUser.client_id)
        .eq('status', 'approved'),
    ]);

    if (pendingResult.error) throw pendingResult.error;

    const submissions: Record<string, WaTemplateSubmission> = {};
    if (pendingResult.data) {
      for (const row of pendingResult.data) {
        const existing = submissions[row.template_key];
        if (!existing) {
          submissions[row.template_key] = {
            id: row.id,
            campaign_id: row.campaign_id,
            template_key: row.template_key,
            submitted_text: row.submitted_text,
            submitted_button_text: row.submitted_button_text,
            submitted_button_url: row.submitted_button_url,
            status: row.status,
            rejection_reason: row.rejection_reason,
            submitted_at: row.submitted_at,
            reviewed_at: row.reviewed_at,
          };
        }
      }
    }

    const approvedKeys = [...new Set(
      (approvedResult.data || []).map(r => r.template_key)
    )];

    return { submissions, approvedKeys };
  } catch (error) {
    console.error('Error fetching WA template submissions:', error);
    return { submissions: {}, approvedKeys: [] };
  }
}

export async function submitWaTemplateForApproval(params: {
  templateKey: string;
  text: string;
  buttonText: string;
  buttonUrl: string;
  campaignId?: string | null;
}): Promise<{ success: boolean; submission?: WaTemplateSubmission; error?: string }> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.email) throw new Error('Not authenticated');

    const { data: existing } = await supabase
      .from('whatsapp_templates')
      .select('id')
      .eq('client_id', localUser.client_id)
      .eq('user_id', localUser.email)
      .eq('template_key', params.templateKey)
      .in('status', ['pending_review', 'rejected', 'draft'])
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (existing) {
      const { data, error } = await supabase
        .from('whatsapp_templates')
        .update({
          submitted_text: params.text,
          submitted_button_text: params.buttonText,
          submitted_button_url: params.buttonUrl,
          status: 'pending_review',
          source: 'manual',
          rejection_reason: null,
          reviewed_at: null,
          submitted_at: new Date().toISOString(),
        })
        .eq('id', existing.id)
        .select()
        .maybeSingle();

      if (error) throw error;
      return {
        success: true,
        submission: data ? {
          id: data.id,
          campaign_id: data.campaign_id,
          template_key: data.template_key,
          submitted_text: data.submitted_text,
          submitted_button_text: data.submitted_button_text,
          submitted_button_url: data.submitted_button_url,
          status: data.status,
          rejection_reason: data.rejection_reason,
          submitted_at: data.submitted_at,
          reviewed_at: data.reviewed_at,
        } : undefined,
      };
    }

    const { data, error } = await supabase
      .from('whatsapp_templates')
      .insert({
        campaign_id: params.campaignId || null,
        user_id: localUser.email,
        client_id: localUser.client_id,
        template_key: params.templateKey,
        submitted_text: params.text,
        submitted_button_text: params.buttonText,
        submitted_button_url: params.buttonUrl,
        status: 'pending_review',
        source: 'manual',
      })
      .select()
      .maybeSingle();

    if (error) throw error;
    return {
      success: true,
      submission: data ? {
        id: data.id,
        campaign_id: data.campaign_id,
        template_key: data.template_key,
        submitted_text: data.submitted_text,
        submitted_button_text: data.submitted_button_text,
        submitted_button_url: data.submitted_button_url,
        status: data.status,
        rejection_reason: data.rejection_reason,
        submitted_at: data.submitted_at,
        reviewed_at: data.reviewed_at,
      } : undefined,
    };
  } catch (error) {
    console.error('Error submitting WA template for approval:', error);
    return { success: false, error: 'Failed to submit template for approval' };
  }
}

export function getWaTemplateApprovalStatus(
  hasApprovedContent: boolean,
  submission?: WaTemplateSubmission | null
): WaTemplateApprovalStatus {
  if (submission?.status === 'pending_review') return 'in_review';
  if (submission?.status === 'rejected') return 'rejected';
  if (hasApprovedContent) return 'active';
  return 'draft';
}

export async function getWaCampaignId(): Promise<string | null> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.client_id) return null;

    const { data } = await supabase
      .from('campaigns')
      .select('campaign_id')
      .eq('client_id', localUser.client_id)
      .eq('type', 'whatsapp')
      .maybeSingle();

    return data?.campaign_id || null;
  } catch {
    return null;
  }
}

export async function saveAgentIdentityConfig(
  agentType: AgentType,
  settings: any
): Promise<boolean> {
  try {
    const identity: Record<string, any> = {};
    const rules: { do: any[]; dont: any[] } = { do: [], dont: [] };

    if (agentType === 'call') {
      const s = settings as CallAgentSettings;
      identity.vapi_agent_id = s.vapi_agent_id || null;
      identity.agent_name = s.agent_name || null;
      identity.gender = s.gender || null;
      identity.nationality = s.nationality || [];
      identity.location = s.location || null;
      identity.tone = s.tone ? normalizeToneValue(s.tone) : null;
      identity.tone_instructions = getToneInstructions(agentType, s.tone || 'friendly_casual');
      identity.role = s.role || null;
      identity.objective = s.objective || null;
      identity.phone_number = s.phone_number || null;
      identity.phone_number_id = s.phone_number_id || null;
      identity.calling_number = s.calling_number || null;
      identity.max_calls_per_lead = s.max_calls_per_lead || null;
      rules.do = s.do_rules || [];
      rules.dont = s.dont_rules || [];
    } else if (agentType === 'whatsapp') {
      const s = settings as WhatsAppAgentSettings;
      identity.agent_name = s.agent_name || null;
      identity.gender = s.gender || null;
      identity.nationality = s.nationality || [];
      identity.location = s.location || null;
      identity.languages = s.languages || null;
      identity.tone = s.tone ? normalizeToneValue(s.tone) : null;
      identity.tone_instructions = getToneInstructions(agentType, s.tone || 'friendly_casual');
      identity.role = s.role || null;
      identity.objective = s.objective || null;
      identity.phone_number = s.phone_number || null;
      identity.whatsapp_account_id = s.whatsapp_account_id || null;
      identity.voice_notes_allowed = s.voice_notes_allowed ?? null;
      identity.voice_note_example = s.voice_note_example || null;
      identity.max_follow_ups = s.max_follow_ups || null;
      rules.do = s.do_rules || [];
      rules.dont = s.dont_rules || [];
    } else if (agentType === 'email') {
      const s = settings as EmailAgentSettings;
      identity.agent_name = s.agent_name || null;
      identity.gender = s.gender || null;
      identity.nationality = s.nationality || [];
      identity.location = s.location || null;
      identity.languages = s.languages || null;
      identity.role = s.role || null;
      identity.objective = s.objective || null;
      identity.from_email = s.from_email || null;
      identity.tone = s.tone ? normalizeToneValue(s.tone) : null;
      identity.tone_instructions = getToneInstructions(agentType, s.tone || 'professional');
      identity.email_layout = s.email_layout || null;
      identity.domain = s.domain || null;
      identity.signature = s.signature || null;
      identity.max_emails = s.max_emails || null;
      identity.email_connection_id = s.email_connection_id || null;
      rules.do = s.do_rules || [];
      rules.dont = s.dont_rules || [];
    } else if (agentType === 'linkedin') {
      const s = settings as LinkedInAgentSettings;
      identity.agent_persona = s.agent_persona || null;
      identity.languages = s.languages || null;
      identity.tone = s.tone ? normalizeToneValue(s.tone) : null;
      identity.tone_instructions = getToneInstructions(agentType, s.tone || 'friendly_casual');
      identity.role = s.role || null;
      identity.objective = s.objective || null;
      identity.max_messages = s.max_messages || null;
      identity.linkedin_account_id = s.linkedin_account_id || null;
      identity.location = s.location || null;
      identity.voice_notes_allowed = s.voice_notes_allowed ?? null;
      identity.voice_note_example = s.voice_note_example || null;
      rules.do = s.do_rules || [];
      rules.dont = s.dont_rules || [];
    }

    await upsertAgentSettingsRow(agentType, {
      agents_identity: identity,
      agent_rules: rules,
    });

    if (agentType === 'call' && identity.vapi_agent_id && identity.agent_name) {
      await supabase
        .from('vapi_agents')
        .update({ agent_name: identity.agent_name })
        .eq('vapi_agent_internal_id', identity.vapi_agent_id);
    }

    return true;
  } catch (error) {
    console.error(`Error saving ${agentType} identity config:`, error);
    return false;
  }
}

export async function generateAgentScript(
  request: AgentScriptGenerationRequest
): Promise<AgentScriptGenerationResponse | null> {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    const response = await fetch(`${supabaseUrl}/functions/v1/generate-agent-script`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) throw new Error('Failed to generate script');

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error generating agent script:', error);
    return null;
  }
}

export function triggerReplyPromptGeneration(agentType: 'whatsapp' | 'email' | 'linkedin') {
  try {
    const user = getLocalUser();
    if (!user?.client_id || !user?.email) return;

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    fetch(`${supabaseUrl}/functions/v1/generate-reply-prompts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`,
      },
      body: JSON.stringify({
        client_id: user.client_id,
        user_id: user.email,
        agent_type: agentType,
      }),
    }).catch((err) => console.log('Reply prompt generation background task:', err));
  } catch {
    // Silent - fire and forget
  }
}
